import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Container from '@/components/base/Container';
import PageHeader from '@/components/base/PageHeader';
import Reveal from '@/components/base/Reveal';
import { articles } from '@/mocks/site';

export default function InsightsPage() {
  const categories = useMemo(() => ['All', ...Array.from(new Set(articles.map((a) => a.category)))], []);
  const [active, setActive] = useState('All');
  const list = active === 'All' ? articles : articles.filter((a) => a.category === active);
  const [feature, ...rest] = list;

  return (
    <SiteLayout title="Insights | AVENOR Accounting & Advisory">
      <PageHeader
        index="05"
        label="Perspectives"
        title="Ideas worth considering."
        intro="Occasional notes from the partners on tax, reporting, assurance and the decisions our clients face. General commentary, not individual advice."
        letter="I"
      />

      <section className="border-t border-background-200 bg-background-50 pb-24 pt-10 md:pb-32">
        <Container>
          <div className="flex flex-wrap gap-x-6 gap-y-3 border-b border-foreground-950 pb-4" role="tablist" aria-label="Filter by category">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                role="tab"
                aria-selected={active === c}
                onClick={() => setActive(c)}
                className={`cursor-pointer whitespace-nowrap font-label text-[11px] uppercase tracking-[0.14em] transition-colors ${active === c ? 'text-primary-500' : 'text-foreground-700 hover:text-foreground-950'}`}
              >
                {c}
              </button>
            ))}
          </div>

          {feature && (
            <Reveal key={feature.slug} className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-6">
              <div className="font-label text-[11px] uppercase tracking-[0.14em] text-foreground-700 lg:col-span-2">
                <p className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-primary-500" />{feature.category}</p>
                <p className="mt-3">{feature.date}</p>
              </div>
              <Link to={`/insights/${feature.slug}`} className="group block cursor-pointer lg:col-span-8">
                <div className="aspect-[16/9] w-full overflow-hidden bg-background-200">
                  <img src={feature.image} alt={feature.title} title={feature.title} className="editorial-img h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.02]" />
                </div>
                <h2 className="mt-8 text-3xl font-light leading-[1.15] tracking-[-0.01em] md:text-[44px]"><span className="ed-link">{feature.title}</span></h2>
                <p className="mt-5 max-w-2xl text-[15px] leading-[1.7] text-foreground-700 md:text-base">{feature.excerpt}</p>
              </Link>
            </Reveal>
          )}

          <div className="mt-20 grid grid-cols-1 gap-y-14 md:grid-cols-2">
            {rest.map((a, i) => (
              <Reveal key={a.slug} delay={(i % 2) * 100} className={`border-background-300/80 ${i % 2 ? 'md:border-l md:pl-8' : 'md:pr-8'}`}>
                <Link to={`/insights/${a.slug}`} className="group block cursor-pointer">
                  <div className="flex items-baseline justify-between font-label text-[11px] uppercase tracking-[0.14em] text-foreground-700">
                    <span className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-foreground-950" />{a.category}</span>
                    <span>{a.date}</span>
                  </div>
                  <div className="mt-6 aspect-[3/2] w-full overflow-hidden bg-background-200">
                    <img src={a.image} alt={a.title} title={a.title} className="editorial-img h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.02]" />
                  </div>
                  <h3 className="mt-6 text-xl font-normal leading-snug md:text-2xl"><span className="ed-link">{a.title}</span></h3>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-700">{a.excerpt}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>
    </SiteLayout>
  );
}